import { Button } from "@components/button";
import { Input } from "@components/input";
import { FlexLoader } from "@components/loader";
import { Modal } from "@components/modal";
import { useAdminUsers } from "@features/admin/users";
import {
  AddMerchantToPlatformRequest,
  AddTransactionToPlatformRequest,
  AdminUserTypes,
  CommissionConfig,
  EditMerchantToPlatformRequest,
  SetPlatformTeamleadComissionRequest,
} from "@features/admin/users/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import * as yup from "yup";

const addScheme = yup.object().shape({
  commission_config: yup.object().shape({
    commission_sbp: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
    commission_card: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
    commission_account_number: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
    commission_ibt: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
  }),
  merchant_id: yup.string().required("ID мерчанта обязателен"),
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // control: Control<UserInfoUpdateRequest, unknown>;
};

export const AddMerchantModal = ({ isOpen, onClose }: Props) => {
  const { selectedUserId } = useParams();
  const { control, handleSubmit } = useForm<AddMerchantToPlatformRequest>({
    resolver: yupResolver(addScheme),
  });
  const { addMerchantToPlatform, getAdminUserInfo, state } = useAdminUsers();
  const handleFormSubmit = (data: AddMerchantToPlatformRequest) => {
    if (!selectedUserId) return;
    addMerchantToPlatform(selectedUserId, data).then(() => {
      onClose();
      getAdminUserInfo(selectedUserId);
    });
  };
  const {
    fields: sbpFields,
    append: appendSbp,
    remove: removeSbp,
  } = useFieldArray({
    control,
    name: "commission_config.commission_sbp",
  });

  const {
    fields: cardFields,
    append: appendCard,
    remove: removeCard,
  } = useFieldArray({
    control,
    name: "commission_config.commission_card",
  });
  const {
    fields: accountFields,
    append: appendAccount,
    remove: removeAccount,
  } = useFieldArray({
    control,
    name: "commission_config.commission_account_number",
  });
  const {
    fields: ibtFields,
    append: appendIBT,
    remove: removeIBT,
  } = useFieldArray({
    control,
    name: "commission_config.commission_ibt",
  });
  const addSbpTier = () => {
    const lastMax =
      sbpFields.length > 0 ? sbpFields[sbpFields.length - 1].max_amount : 0;
    appendSbp({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };

  const addCardTier = () => {
    const lastMax =
      cardFields.length > 0 ? cardFields[cardFields.length - 1].max_amount : 0;
    appendCard({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };

  const addAccountTier = () => {
    const lastMax =
      accountFields.length > 0
        ? accountFields[accountFields.length - 1].max_amount
        : 0;
    appendAccount({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };

  const addIBTTier = () => {
    const lastMax =
      ibtFields.length > 0 ? ibtFields[ibtFields.length - 1].max_amount : 0;
    appendIBT({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={"Добавить мерчанта на платформу"}
    >
      {state.platformModalLoading ? (
        <FlexLoader style={{ width: "300px", height: "300px" }} />
      ) : (
        <form>
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">SBP Commissions</h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {sbpFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_sbp.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_sbp.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_sbp.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSbp(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSbpTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Card Commissions</h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {cardFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_card.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_card.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_card.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCard(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addCardTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              Account Number Commissions
            </h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {accountFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_account_number.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_account_number.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_account_number.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAccount(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addAccountTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">IBT Commissions</h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {ibtFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_ibt.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_ibt.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_ibt.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeIBT(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addIBTTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>
          <div style={{ marginBottom: "10px" }}>
            <Input
              label="ID мерчанта"
              control={control}
              type="text"
              name={`merchant_id`}
            />
          </div>
          <Button onClick={handleSubmit(handleFormSubmit)}>Добавить</Button>
        </form>
      )}
    </Modal>
  );
};

type EditMerchantForm = {
  commission_config: CommissionConfig;
};

type EditProps = Props & {
  merchant_id: string;
};

const editScheme = yup.object().shape({
  commission_config: yup.object().shape({
    commission_sbp: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
    commission_card: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
    commission_account_number: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
    commission_ibt: yup
      .array()
      .of(
        yup.object().shape({
          min_amount: yup.number().required(),
          max_amount: yup.number().required(),
          rate: yup.number().required(),
        })
      )
      .required(),
  }),
});

export const EditMerchantModal = ({
  isOpen,
  onClose,
  merchant_id,
}: EditProps) => {
  const { selectedUserId } = useParams();
  const { control, handleSubmit, reset } = useForm<EditMerchantForm>({
    resolver: yupResolver(editScheme),
  });
  const { editMerchantToPlatform, getAdminUserInfo, state } = useAdminUsers();

  const handleFormSubmit = (data: EditMerchantForm) => {
    if (!selectedUserId) return;
    if (state.userInfo?.kind != AdminUserTypes.Platform) return;

    const merchantsData = state.userInfo.merchants.map((merchant) => ({
      merchant_id: merchant.id,
      commission_config: merchant.commission_config,
    }));
    merchantsData.forEach((merchant) => {
      if (merchant.merchant_id === merchant_id) {
        merchant.commission_config = data.commission_config;
      }
    });

    const bodyData: EditMerchantToPlatformRequest = {
      merchants: merchantsData,
    };

    editMerchantToPlatform(selectedUserId, bodyData).then(() => {
      onClose();
      getAdminUserInfo(selectedUserId);
    });
  };

  const {
    fields: sbpFields,
    append: appendSbp,
    remove: removeSbp,
    replace: replaceSbp,
  } = useFieldArray({
    control,
    name: "commission_config.commission_sbp",
  });

  const {
    fields: cardFields,
    append: appendCard,
    remove: removeCard,
    replace: replaceCard,
  } = useFieldArray({
    control,
    name: "commission_config.commission_card",
  });
  const {
    fields: accountFields,
    append: appendAccount,
    remove: removeAccount,
    replace: replaceAccount,
  } = useFieldArray({
    control,
    name: "commission_config.commission_account_number",
  });
  const {
    fields: ibtFields,
    append: appendIBT,
    remove: removeIBT,
  } = useFieldArray({
    control,
    name: "commission_config.commission_ibt",
  });

  useEffect(() => {
    if (state.userInfo?.kind != AdminUserTypes.Platform) return;
    const comission = state.userInfo.merchants.find(
      (merchant) => merchant.id == merchant_id
    )?.commission_config;

    if (comission) {
      // reset({ commission_config: comission });
      replaceAccount(comission.commission_account_number);
      // comission.commission_account_number.
      replaceCard(comission.commission_card);
      replaceSbp(comission.commission_sbp);
    }
  }, [merchant_id, state?.userInfo, reset]);

  const addSbpTier = () => {
    const lastMax =
      sbpFields.length > 0 ? sbpFields[sbpFields.length - 1].max_amount : 0;
    appendSbp({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };

  const addCardTier = () => {
    const lastMax =
      cardFields.length > 0 ? cardFields[cardFields.length - 1].max_amount : 0;
    appendCard({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };

  const addAccountTier = () => {
    const lastMax =
      accountFields.length > 0
        ? accountFields[accountFields.length - 1].max_amount
        : 0;
    appendAccount({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };

  const addIBTTier = () => {
    const lastMax =
      ibtFields.length > 0 ? ibtFields[ibtFields.length - 1].max_amount : 0;
    appendIBT({ min_amount: lastMax, max_amount: 0, rate: 0 });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={"Изменить комиссию мерчанта"}
    >
      {state.platformModalLoading ? (
        <FlexLoader style={{ width: "300px", height: "300px" }} />
      ) : (
        <form>
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">SBP Commissions</h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {sbpFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_sbp.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_sbp.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_sbp.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSbp(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSbpTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Card Commissions</h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {cardFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_card.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_card.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_card.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCard(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addCardTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              Account Number Commissions
            </h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {accountFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_account_number.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_account_number.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_account_number.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAccount(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addAccountTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">IBT Commissions</h2>
            <div className="flex overflow-x-auto space-x-4 pb-4 max-w-md">
              {ibtFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex-shrink-0 w-50 bg-white p-4 rounded shadow"
                >
                  <div className="mb-2">
                    <Input
                      label="Min Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_ibt.${index}.min_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Max Amount"
                      control={control}
                      type="number"
                      name={`commission_config.commission_ibt.${index}.max_amount`}
                    />
                  </div>
                  <div className="mb-2">
                    <Input
                      label="Rate"
                      control={control}
                      type="number"
                      name={`commission_config.commission_ibt.${index}.rate`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeIBT(index)}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addIBTTier}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Tier
            </button>
          </section>
          <div style={{ marginBottom: "10px" }}>
            <Input
              label="ID мерчанта"
              control={control}
              defaultValue={merchant_id}
              disabled
              type="text"
              name={`merchant_id`}
            />
          </div>
          <Button onClick={handleSubmit(handleFormSubmit)}>Добавить</Button>
        </form>
      )}
    </Modal>
  );
};

const transactionScheme = yup.object().shape({
  amount: yup.number().required("Комиссия обязательна"),
  transaction_hash: yup.string().required("Хэш обязателен"),
  wallet: yup.string().required("Кошелек обязателен"),
});

export const AddTransactionModal = ({ isOpen, onClose }: Props) => {
  const { selectedUserId } = useParams();
  const { control, handleSubmit } = useForm<AddTransactionToPlatformRequest>({
    resolver: yupResolver(transactionScheme),
  });
  const { addTransactionToPlatform, getPlatformTransactions, state } =
    useAdminUsers();
  const handleFormSubmit = (data: AddTransactionToPlatformRequest) => {
    if (!selectedUserId) return;
    if (state.userInfo?.kind != AdminUserTypes.Platform) return;

    addTransactionToPlatform(selectedUserId, data).then(() => {
      onClose();
      getPlatformTransactions(selectedUserId);
    });
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={"Добавить транзакцию"}>
      {state.platformModalLoading ? (
        <FlexLoader style={{ width: "300px", height: "300px" }} />
      ) : (
        <form>
          <div style={{ marginBottom: "10px" }}>
            <Input
              label="Сумма"
              control={control}
              type="number"
              name={`amount`}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Input
              label="Хэш транзакции"
              control={control}
              type="text"
              name={`transaction_hash`}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Input
              label="Кошелек"
              control={control}
              type="text"
              name={`wallet`}
            />
          </div>
          <Button onClick={handleSubmit(handleFormSubmit)}>Изменить</Button>
        </form>
      )}
    </Modal>
  );
};

const teamleadComissionScheme = yup.object().shape({
  referrer_email: yup.string().required("Email обязательна"),
  team_lead_commission_pay_in: yup.number().required("Комиссия обязателен"),
});

export const SetTeamleadComissionModal = ({ isOpen, onClose }: Props) => {
  const { selectedUserId } = useParams();

  const { control, handleSubmit } = useForm<SetPlatformTeamleadComissionRequest>({
    resolver: yupResolver(teamleadComissionScheme),
  });
  const { setPlatformTeamleadComission, state } =
    useAdminUsers();

  const handleFormSubmit = (data: SetPlatformTeamleadComissionRequest) => {
    if (!selectedUserId) return;
    if (state.userInfo?.kind != AdminUserTypes.Platform) return;

    setPlatformTeamleadComission(selectedUserId, data).then(() => {
      onClose();
      // getPlatformTransactions(selectedUserId);
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={"Комиссия агента"}>
      {state.platformModalLoading ? (
        <FlexLoader style={{ width: "300px", height: "300px" }} />
      ) : (
        <form>
          <div style={{ marginBottom: "10px" }}>
            <Input
              label="Почта агента"
              control={control}
              type="text"
              name={`referrer_email`}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Input
              label="Сумма"
              control={control}
              type="number"
              name={`team_lead_commission_pay_in`}
            />
          </div>
          <Button onClick={handleSubmit(handleFormSubmit)}>Сохранить</Button>
        </form>
      )}
    </Modal>
  );
};
